import { Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import CarRentalIcon from '@mui/icons-material/CarRental';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import MissionRentService from '../../../services/MissionRentService';
import { validateRentForm } from '../../../helpers/ValidateForm';
import formatWithThousandSeparator from '../../../helpers/FormatWithThousandSeparator';
import { LoadingButton } from '@mui/lab';
import { makeStyles } from '@material-ui/core/styles';
import VehicleService from '../../../services/VehicleService';

const useStyles = makeStyles((theme) => ({
  formArea: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(248, 251, 255, 0.3)',
      borderRadius: 10,
      transition: 'box-shadow 0.2s ease, border-color 0.2s ease'
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: '#cbd5e1'
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1e88e5'
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1e40af',
      boxShadow: '0 0 0 3px rgba(30, 64, 175, 0.12)'
    },
    '& .MuiInputBase-input': {
      padding: '10px 12px',
      fontSize: 13
    },
    
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#1e40af'
    }
  },
  formLabel: {
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
    color: '#1e3a8a',
    gap: 4,
    marginBottom: 4,
    fontSize: 12
  },
  dialogPaperCompact: {
    borderRadius: 16,
    border: '1px solid #c7d2fe',
    background: 'linear-gradient(135deg, #ffffff 0%, #f6f8ff 100%)',
    boxShadow: '0 14px 28px rgba(30, 64, 175, 0.18)'
  },
  dialogTitle: {
    color: '#ffffff',
    fontWeight: 700,
    letterSpacing: 0.4,
    padding: theme.spacing(2, 3)
  },
  dialogContent: {
    backgroundColor: '#fbfcff',
    padding: theme.spacing(2.5, 3)
  },
  dialogText: {
    color: '#1f2937',
    fontWeight: 500
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: '1px solid #d8e2f0',
    justifyContent: 'space-between'
  },
  actionButton: {
    fontWeight: 700,
    textTransform: 'none',
    borderRadius: 10,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3)
  }
}));

const bullRed = (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      mx: "5px",
      transform: "scale(0.8)",
      color: "red",
      fontSize: "20px",
    }}
  >
    *
  </Box>
);

const RentManagement = ({ setMessage, setValue, setTransportMeans, mission_planning_agenda_id }) => {
  const classes = useStyles();
  const [rents, setRents] = useState([]);
  const [disabledSubmit, setDisabledSubmit] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, mission_rent_id: null });
  const transportMeansInitial = [
    { label: 'Voiture FID', value: 'FID', mission_rent_id: null, rent_upfuel: null, rent_amount: null, rent_paiement: null },
    { label: 'Avion', value: 'AVION', mission_rent_id: null, rent_upfuel: null, rent_amount: null, rent_paiement: null },
    { label: 'Bateau', value: 'BATEAU', mission_rent_id: null, rent_upfuel: null, rent_amount: null, rent_paiement: null },
  ];
  const [currentFuelPrice, setCurrentFuelPrice] = useState(null);
  const RENT_TYPE = [
    { label: 'Location voiture', value: 'Location voiture' },
    { label: 'Location moto', value: 'Location moto' },
    { label: 'Location velo', value: 'Location velo' },
    { label: 'A determiner', value: 'other' },
  ];
  const PAIEMENT_TYPE = [
    { label: 'Forfaitaire', value: 'flat' },
    { label: 'Paiement par jour', value: 'daily' },
  ];
  const initRentForm = {
    mission_rent_id: null,
    rent_type: '',
    rent_paiement: null,
    rent_amount: null,
    rent_upfuel: null,
    rent_designation: '',
    mission_planning_agenda_id: mission_planning_agenda_id,
    rent_conso_100km: null,
  };
  const [rentForm, setRentForm] = useState(initRentForm);
  const closeDeleteDialog = () => setDeleteDialog({ open: false, mission_rent_id: null });

  const fetchRents = async () => {
    try {
      const result = await MissionRentService.getRentsByAgendaId(mission_planning_agenda_id);
      if (result && result.status) {
        const rentsResult = result.data || [];
        setRents(rentsResult.map((rent) => ({
          rent_type: RENT_TYPE.find((type) => type.value === rent.rent_type) ? rent.rent_type : 'other',
          rent_paiement: rent.rent_paiement,
          rent_amount: rent.rent_amount,
          rent_upfuel: rent.rent_upfuel,
          mission_rent_id: rent.mission_rent_id,
          rent_conso_100km: rent.rent_conso_100km,
          mission_planning_agenda_id: rent.mission_planning_agenda_id,
          rent_designation: RENT_TYPE.find((type) => type.value === rent.rent_type) ? '' : rent.rent_type,
        })));

     setTransportMeans((prev) => {
      const baseOptions = prev.filter((option) => option.mission_rent_id == null);
      const transportOptions = rentsResult.map((rent) => ({
        label: rent.rent_type,
        value: rent.rent_type,
        mission_rent_id: rent.mission_rent_id,
        rent_amount: rent.rent_amount,
        rent_upfuel: rent.rent_upfuel,
        rent_conso_100km: rent.rent_conso_100km,
        rent_paiement: rent.rent_paiement,
      }));

      return [...baseOptions, ...transportOptions];
    });

      }
    } catch (error) {
      console.error('Error fetching rents:', error);
    }
  };

  useEffect(() => {
    if (mission_planning_agenda_id) {
      fetchRents();
      fetchCurrentFuelPrice()
    }
  }, [mission_planning_agenda_id]);

  const handleDeleteRent = async (mission_rent_id) => {
    try {
      const result = await MissionRentService.deleteRentById(mission_rent_id);
      if (result && result.status) {
        closeDeleteDialog();
        setMessage({ open: true, color: 'green', messageInfo: 'Location supprimee avec succes.' });
        await fetchRents();
      }
    } catch (error) {
      console.error('Error deleting rent:', error);
    }
  };

  const openCreateDialog = () => {
    setFormMode('create');
    setRentForm(initRentForm);
    setFormDialogOpen(true);
  };

  const openEditDialog = (rent) => {
    const isCarRent = rent.rent_type === 'Location voiture';
    setFormMode('edit');
    setRentForm({
      mission_rent_id: rent.mission_rent_id,
      rent_type: RENT_TYPE.find((type) => type.value === rent.rent_type) ? rent.rent_type : 'other',
      rent_paiement: rent.rent_paiement,
      rent_amount: rent.rent_amount,
      rent_upfuel: isCarRent ? rent.rent_upfuel : null,
      rent_designation: RENT_TYPE.find((type) => type.value === rent.rent_type) ? '' : rent.rent_type,
      mission_planning_agenda_id: rent.mission_planning_agenda_id,
      rent_conso_100km: isCarRent ? (rent.rent_conso_100km ?? 16) : null,
    });
    setFormDialogOpen(true);
  };

  const closeFormDialog = () => {
    setFormDialogOpen(false);
    setRentForm(initRentForm);
    setFormMode('create');
  };

  const handleSubmitRentForm = async () => {
    setDisabledSubmit(true);
    const validationResult = validateRentForm(rentForm);
    if (!validationResult.valid) {
      setMessage({ open: true, color: 'red', messageInfo: validationResult.message });
      setDisabledSubmit(false);
      return;
    }

    try {
      const isCarRent = rentForm.rent_type === 'Location voiture';
      const dataToSend = {
        mission_rent_id: rentForm.mission_rent_id,
        rent_type: rentForm.rent_type === 'other' ? rentForm.rent_designation : rentForm.rent_type,
        rent_paiement: rentForm.rent_paiement,
        rent_amount: rentForm.rent_amount,
        rent_upfuel: isCarRent ? rentForm.rent_upfuel : null,
        mission_planning_agenda_id: rentForm.mission_planning_agenda_id,
        rent_conso_100km: isCarRent ? rentForm.rent_conso_100km : null,
      };

      const result = await MissionRentService.addMissionRent(dataToSend);
      if (result && result.status) {
        setMessage({ open: true, color: 'green', messageInfo: 'Location enregistree avec succes.' });
        await fetchRents();
        closeFormDialog();
      }
    } catch (error) {
      console.error('Error submitting rent:', error);
    } finally {
      setDisabledSubmit(false);
    }
  };

  const handleRemoveRent = (mission_rent_id) => {
    if (mission_rent_id) {
      setDeleteDialog({ open: true, mission_rent_id });
    }
  };

  const fetchCurrentFuelPrice = async () => {
    try {
      const result = await VehicleService.currentFuelPrice('diesel');
      if (result && result.status) {
        setCurrentFuelPrice(result.data);
        return result.data;
      }
    } catch (error) { 
      console.error('Error fetching current fuel price:', error);
      return null;
    }
  };

  const getRentTypeLabel = (rent) => (rent.rent_type === 'other' ? rent.rent_designation : rent.rent_type || '-');
  const getPaymentLabel = (value) => PAIEMENT_TYPE.find((p) => p.value === value)?.label || '-';

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Button color='error' ml={2} variant="contained" sx={{ marginLeft: '10px' }} startIcon={<KeyboardBackspaceIcon />} size="small" onClick={() => setValue('1')}>
            Retour
          </Button>
          <Button color='info'  sx={{marginLeft: "10px"}}  variant="contained"  startIcon={<CarRentalIcon />} size="small" onClick={openCreateDialog}>
            Ajouter une location
          </Button>
        </Grid>
        <Grid item xs={12}>
          {rents && rents.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="22%">Type de location </TableCell>
                    <TableCell width="14%">Mode de paiement </TableCell>
                    <TableCell width="14%">Tarif (Ar)</TableCell>
                    <TableCell width="14%">Prix carburant (Ar/L) </TableCell>
                    <TableCell width="18%">Consommation (L/100km)</TableCell>
                    <TableCell width="14%" align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rents.map((rent, index) => (
                    <TableRow key={rent.mission_rent_id ?? `rent-${index}`}>
                      <TableCell>{rent.rent_type === 'other' ? rent.rent_designation : rent.rent_type}</TableCell>
                      <TableCell>{getPaymentLabel(rent.rent_paiement)}</TableCell>
                      <TableCell>{formatWithThousandSeparator(rent.rent_amount, { fallback: '-' })}</TableCell>
                      <TableCell>{formatWithThousandSeparator(rent.rent_upfuel, { fallback: '-' })}</TableCell>
                      <TableCell>{formatWithThousandSeparator(rent?.rent_conso_100km, { fallback: '-' })}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEditDialog(rent)}>
                          <ModeEditOutlineIcon color="info" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleRemoveRent(rent.mission_rent_id)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper variant="outlined" sx={{ height: '20vh', mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="textSecondary">Aucune location a afficher</Typography>
            </Paper>
          )}
          
        </Grid>
      </Grid>

      <Dialog
        open={formDialogOpen}
        onClose={closeFormDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.dialogPaperCompact }}
      >
        <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
          {formMode === 'create' ? 'Ajouter une location' : 'Modifier la location'}
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <DialogContentText component="div" className={`${classes.dialogText} ${classes.formArea}`}>
            <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <label className={classes.formLabel}>Type de location {bullRed}</label>
                <Autocomplete
                  options={RENT_TYPE}
                  getOptionLabel={(option) => option.label}
                  value={RENT_TYPE.find((t) => t.value === rentForm.rent_type) || null}
                  onChange={(_, newValue) => {
                    const nextType = newValue ? newValue.value : '';
                    const shouldClearFuel = nextType !== 'Location voiture';
                    setRentForm((prev) => ({
                      ...prev,
                      rent_type: nextType,
                      ...(shouldClearFuel ? { rent_upfuel: null, rent_conso_100km: null } : {rent_upfuel: prev.rent_upfuel ?? currentFuelPrice, rent_conso_100km: prev.rent_conso_100km ?? 16}),
                    }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Type de location" size="small" required />}
                />
              </FormControl>
            </Grid>
            {rentForm.rent_type === 'other' && (
              <Grid item xs={12}>
                <label className={classes.formLabel}>Designation {bullRed}</label>
                <TextField
                  label="Designation"
                  size="small"
                  fullWidth
                  value={rentForm.rent_designation}
                  onChange={(e) => setRentForm((prev) => ({ ...prev, rent_designation: e.target.value }))}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <label className={classes.formLabel}>Mode de paiement {bullRed}</label>
                <Autocomplete
                  options={PAIEMENT_TYPE}
                  getOptionLabel={(option) => option.label}
                  value={PAIEMENT_TYPE.find((p) => p.value === rentForm.rent_paiement) || null}
                  onChange={(_, newValue) => setRentForm((prev) => ({ ...prev, rent_paiement: newValue ? newValue.value : '' }))}
                  renderInput={(params) => <TextField {...params} label="Mode de paiement" size="small" required />}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12}>
              <label className={classes.formLabel}>Tarif (Ar) {bullRed}</label>
              <TextField
                label="Tarif (Ar)"
                type="number"
                size="small"
                fullWidth
                value={rentForm.rent_amount ?? ''}
                onChange={(e) => setRentForm((prev) => ({ ...prev, rent_amount: e.target.value }))}
              />
            </Grid>
            {rentForm.rent_type === 'Location voiture' && (
              <>
                <Grid item xs={12} sm={6}>
                  <label className={classes.formLabel}>Prix carburant (Ar/L)</label>
                  <TextField
                    label="Prix carburant (Ar/L)"
                    type="number"
                    size="small"
                    fullWidth
                    value={rentForm.rent_upfuel ?? ''}
                    onChange={(e) => setRentForm((prev) => ({ ...prev, rent_upfuel: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <label className={classes.formLabel}>Consommation (L/100 km)</label>
                  <TextField
                    label="Consommation (L/100 km)"
                    type="number"
                    size="small"
                    fullWidth
                    value={rentForm.rent_conso_100km ?? ''}
                    onChange={(e) => setRentForm((prev) => ({ ...prev, rent_conso_100km: e.target.value }))}
                  />
                </Grid>
              </>
            )}
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={closeFormDialog} size="small" variant='contained' color='error' className={classes.actionButton}>Annuler</Button>
          <LoadingButton
            loading={disabledSubmit}
            loadingPosition="start"
            variant="contained"
            color="info"
            size="small"
            onClick={handleSubmitRentForm}
            className={classes.actionButton}
          >
            Enregistrer
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Etes-vous sur de vouloir supprimer cette location ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary" size="small">
            Annuler
          </Button>
          <Button onClick={() => handleDeleteRent(deleteDialog.mission_rent_id)} color="error" size="small" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RentManagement;
